import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Check, ChevronLeft, Loader2, Lock, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { ActionButton, Field, SecondaryAction } from "../kit";
import LoadFailedCard from "./LoadFailedCard";
import { getGame, savePlan, publish, gameWhenLine } from "../../lib/coachGames";
import useSaveBeforeLeave from "../../lib/useSaveBeforeLeave";
import { TOSS_CHOICES, planFromServer, planBody, teamSummary } from "../../lib/pickXiPlan";

const REMEMBER_MAX = 120;
const PLAN_TEXT_MAX = 2000;
const THREE_PLACEHOLDERS = ["e.g. Back up every throw", "Focus point", "Focus point"];

const sheetPath = (gameId) => `/coach-portal/team?game=${encodeURIComponent(gameId)}`;

/**
 * Issue #1318 (Pick XI coach 3 of 3; D297, D299, D300, D302, D309, D317) —
 * Game plan & notes, step 2 of 2, drawn in `GamePlanNotes.dc.html` (round 13,
 * Martin's mockup 2): the match header, Your team with Edit, the toss call, the
 * three things to remember, the game plan, the general notes, Who sees what,
 * and the two actions.
 *
 * Save as draft (`PUT /plan`) sends nothing to anyone. Publish team
 * (`POST /publish`) tells the squad. If the draft sheet has no captain or no
 * keeper, the first press shows `plan-publish-check` and the second publishes:
 * it asks once and never blocks. A failed save or publish keeps every field on
 * screen (D57). Players see the game plan is one box, ticked to start; parents
 * and caregivers are left out because there is no parent role (D297). The
 * volunteers show here and never reach a player (D300).
 *
 * D317: where the board prints [ROUND], this prints the game's competition,
 * because Schedule a game captures no round.
 *
 * States, each its own (D53): `loading`, `failed` (Try again) and the form.
 */
export default function GamePlanNotes({ gameId }) {
  const [load, setLoad] = useState({ status: "loading" }); // loading | failed | ready
  const [game, setGame] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("idle"); // idle | saving | publishing
  // null | draft-saved | draft-failed | published | failed | check. `published` remembers the tick it was sent with.
  const [notice, setNotice] = useState(null);
  const [sentWithPlan, setSentWithPlan] = useState(true);
  const formRef = useRef(form);
  formRef.current = form;
  // D57, D331 (#1344): the way back, any other in-app link, sign out, the demo switch, the role switch and
  // a reload must not lose what was typed. When something was typed, save the draft plan first, then go; if the
  // save fails, stay here with the failure card and every field (nothing is lost, and Save as draft and Publish
  // are still there). A modified click (new tab) and an untouched form are left as they are.
  const [leaveFailed, setLeaveFailed] = useState(0); // counts failed leave-saves, to bring the failed card into view
  const leave = useSaveBeforeLeave({
    onLeaveFailed: () => setLeaveFailed((n) => n + 1),
    save: async () => {
      setBusy("saving");
      setNotice(null);
      try {
        await savePlan(gameId, planBody(formRef.current));
      } catch (err) {
        console.error("GamePlanNotes: failed to save the plan before leaving", err);
        setNotice("draft-failed");
        throw err;
      } finally {
        setBusy("idle");
      }
    },
  });
  const { edited } = leave;
  // A coach who pressed Sign out from the top bar must see why nothing happened: the failed card sits after the form.
  useEffect(() => {
    if (leaveFailed) document.querySelector('[data-testid="plan-draft-failed"]')?.scrollIntoView?.({ block: "nearest" });
  }, [leaveFailed]);
  const checked = useRef(false); // the publish check has been shown once for this visit
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    setLoad({ status: "loading" });
    checked.current = false;
    getGame(gameId)
      .then((g) => {
        if (!live) return;
        setGame(g);
        setForm(planFromServer(g));
        setLoad({ status: "ready" });
      })
      .catch((e) => {
        if (!live) return;
        console.error("GamePlanNotes: failed to load the team sheet", e);
        setLoad({ status: "failed" });
      });
    return () => {
      live = false;
    };
  }, [gameId, reloadKey]);

  // Any edit takes a finished or failed notice down: it describes what was sent before the edit.
  const edit = useCallback((patch) => {
    edited();
    setForm((f) => ({ ...f, ...patch }));
    setNotice((n) => (n === "draft-saved" || n === "draft-failed" || n === "failed" || n === "published" ? null : n));
  }, [edited]);

  const saveDraft = async () => {
    if (busy !== "idle") return;
    setBusy("saving");
    setNotice(null);
    await leave.persist(async () => {
      try {
        await savePlan(gameId, planBody(form));
        setNotice("draft-saved");
      } catch (e) {
        console.error("GamePlanNotes: failed to save the draft", e);
        setNotice("draft-failed");
        throw e;
      } finally {
        setBusy("idle");
      }
    });
  };

  const summary = game ? teamSummary(game) : null;
  const needsCheck = summary ? summary.needsCheck : true;

  const publishTeam = async () => {
    if (busy !== "idle") return;
    if (needsCheck && !checked.current) {
      checked.current = true;
      setNotice("check");
      return;
    }
    setBusy("publishing");
    setNotice(null);
    await leave.persist(async () => {
      try {
        await publish(gameId, planBody(form));
        setSentWithPlan(!!form.playersSeePlan);
        setNotice("published");
      } catch (e) {
        console.error("GamePlanNotes: failed to publish the team", e);
        setNotice("failed");
        throw e;
      } finally {
        setBusy("idle");
      }
    });
  };

  const back = (
    <SecondaryAction as={Link} to={sheetPath(gameId)} variant="link" data-testid="plan-back" style={{ display: "flex", width: "fit-content", alignItems: "center", gap: 4 }}>
      <ChevronLeft size={16} aria-hidden="true" />
      Pick the XI
    </SecondaryAction>
  );
  // The way back is one element at one place across loading, failed and ready.
  let body;
  if (load.status === "loading") body = <Skeleton />;
  else if (load.status === "failed") {
    body = <LoadFailedCard title="Couldn't load the team sheet" testId="plan-load-failed" retryTestId="plan-load-failed-retry" onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  const publishing = busy === "publishing";
  const set = (key) => (e) => edit({ [key]: e.target.value });
  const setRemember = (i) => (e) => edit({ remember: form.remember.map((v, j) => (j === i ? e.target.value : v)) });

  const renderForm = () => (
    <div data-testid="xi-plan" className="flex flex-col" style={{ gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span className="nw-eyebrow" style={{ whiteSpace: "nowrap" }}>Step 2 of 2</span>
        <span aria-hidden="true" style={{ flex: 1, display: "flex", gap: 4 }}>
          <span style={{ flex: 1, height: 6, borderRadius: 3, background: "var(--nw-navy)" }} />
          <span style={{ flex: 1, height: 6, borderRadius: 3, background: "var(--nw-navy)" }} />
        </span>
      </div>

      <div className="flex flex-col" style={{ gap: 4 }}>
        <div className="nw-title-lg" data-testid="plan-title">Game plan &amp; notes</div>
        <div className="nw-lede">What you want the team thinking about, plus anything players need to know. All optional.</div>
      </div>

      <MatchHeader game={game} />
      <TeamCard summary={summary} gameId={gameId} />

      <SectionHead label="If we win the toss" labelId="plan-toss-label" />
      <TossSwitch value={form.toss} onChange={(toss) => edit({ toss })} />

      <SectionHead label="Three things to remember" />
      <div className="nw-help">Short and punchy. These show at the top of the team sheet.</div>
      <div className="flex flex-col" style={{ gap: 8 }}>
        {form.remember.map((v, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span aria-hidden="true" style={{ flexShrink: 0, width: 28, height: 28, borderRadius: "var(--nw-radius-pill)", background: "var(--nw-navy)", color: "#FFFFFF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>
              {i + 1}
            </span>
            <Field
              className="flex-1 min-w-0"
              aria-label={`Thing to remember ${i + 1}`}
              data-testid={`plan-three-${i + 1}`}
              placeholder={THREE_PLACEHOLDERS[i]}
              value={v}
              maxLength={REMEMBER_MAX}
              autoComplete="off"
              onChange={setRemember(i)}
            />
          </div>
        ))}
      </div>

      <SectionHead label="Game plan" />
      <PlanText id="plan-bat" title="With the bat" hint="Approach, target score, roles for the openers or finishers." minHeight={112} value={form.planBat} onChange={set("planBat")} />
      <PlanText id="plan-ball" title="With the ball & in the field" hint="Lines and lengths, field settings, who's in the ring or on the boundary." minHeight={112} value={form.planBall} onChange={set("planBall")} />
      <PlanText id="plan-opposition" title="About the opposition" hint="Danger players, how they played last time, the ground." minHeight={90} value={form.planOpposition} onChange={set("planOpposition")} />

      <SectionHead label="General notes" />
      <div style={{ width: "50%" }}>
        <Field label="Arrive by" type="time" data-testid="plan-arrive" value={form.arriveBy} onChange={set("arriveBy")} />
      </div>
      <PlanText id="plan-notes" title="Notes for the team" hint="Kit, transport, sunscreen and water, afternoon tea roster." minHeight={90} value={form.teamNotes} onChange={set("teamNotes")} />

      <WhoSees checked={form.playersSeePlan} onToggle={() => edit({ playersSeePlan: !form.playersSeePlan })} />

      {notice === "check" && <PublishCheck gameId={gameId} />}
      {notice === "draft-failed" && <Notice testId="plan-draft-failed" tone="alert" role="alert" title="Couldn't save the draft" body="Nothing was saved. Your notes are still here. Try again." />}
      {notice === "failed" && <Notice testId="plan-publish-failed" tone="alert" role="alert" title="Couldn't publish the team" body="Nothing was sent. Your notes are still here. Try again." />}
      {notice === "published" && (
        <Notice
          testId="plan-published"
          tone="ok"
          role="status"
          title="Team published. The squad has been told."
          body={
            sentWithPlan
              ? "Each player sees the team in Coming up, with the game plan because Players see the game plan is ticked. The volunteers stay with you. Changes after this are sent again when you publish them."
              : `Each player sees the team in Coming up. ${PLAN_KEPT_BACK} Changes after this are sent again when you publish them.`
          }
        />
      )}
      {notice === "draft-saved" && <Notice testId="plan-draft-saved" tone="ok" role="status" title="Draft saved. Nothing has been sent." body="The game shows as Draft in Your games until you publish." />}

      {publishing ? (
        <span
          data-testid="plan-publish-busy"
          aria-busy="true"
          role="status"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, minHeight: 52, background: "var(--nw-line)", color: "var(--nw-ink-lede)", fontWeight: 700, fontSize: 16, borderRadius: "var(--nw-radius-control)" }}
        >
          <Loader2 size={18} className="animate-spin" aria-hidden="true" />
          Publishing…
        </span>
      ) : (
        <ActionButton data-testid="plan-publish" onClick={publishTeam} disabled={busy !== "idle"}>
          Publish team
        </ActionButton>
      )}
      <SecondaryAction variant="navy-outline" data-testid="plan-draft" onClick={saveDraft} disabled={busy !== "idle"}>
        {busy === "saving" ? "Saving…" : "Save as draft"}
      </SecondaryAction>
      <div style={{ fontSize: 13, textAlign: "center", color: "var(--nw-ink-muted)" }}>Publishing notifies the squad. You can still edit after.</div>
    </div>
  );

  return (
    <div className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }}>
      {back}
      {body || renderForm()}
    </div>
  );
}

/** The board's words for a plan kept back: also what sits under the switch when it is unticked. */
const PLAN_KEPT_BACK = "Players get the team and the general notes. The three things, the game plan and the volunteers stay with you.";

function Skeleton() {
  return (
    <div data-testid="plan-loading" aria-busy="true" className="flex flex-col" style={{ gap: 6 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", height: 92, boxSizing: "border-box", borderRadius: "var(--nw-radius-card)", background: "var(--nw-surface)", border: "1px solid var(--nw-line)" }}>
          <div style={{ width: "50%", height: 12, borderRadius: 6, background: "var(--nw-line)" }} />
          <div style={{ width: "30%", height: 10, borderRadius: 5, background: "var(--nw-line-faint)" }} />
        </div>
      ))}
    </div>
  );
}

function SectionHead({ label, labelId }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
      <div className="nw-section" id={labelId} data-testid={labelId}>{label}</div>
    </div>
  );
}

/** "[COMPETITION]", "vs [OPPOSITION]" and "[DAY, DATE] · [START TIME] · [GROUND NAME]" in the navy card. D317: the competition stands where the board prints [ROUND]. */
function MatchHeader({ game }) {
  return (
    <div data-testid="plan-match-header" style={{ display: "flex", flexDirection: "column", gap: 4, padding: 16, background: "var(--nw-navy)", borderRadius: "var(--nw-radius-card)" }}>
      {game.competition && <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "#84C5FF", overflowWrap: "anywhere" }}>{game.competition}</span>}
      <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 22, color: "#FFFFFF", overflowWrap: "anywhere" }}>{`vs ${game.opponent}`}</span>
      <span style={{ fontSize: 13, color: "var(--nw-navy-tint)", overflowWrap: "anywhere" }}>{gameWhenLine(game)}</span>
    </div>
  );
}

const chip = (style, text) => (
  <span style={{ display: "inline-flex", alignItems: "center", padding: "1px 6px", borderRadius: 6, fontSize: 11, fontWeight: 700, ...style }}>{text}</span>
);

/** Your team: the draft sheet in batting order with its tags, the 12th man, the openers and the volunteers (coach only). */
function TeamCard({ summary, gameId }) {
  const rows = summary?.rows || [];
  const half = Math.ceil((rows.length + (summary?.twelfth ? 1 : 0)) / 2);
  const cells = rows.map((r) => (
    <div key={r.n} style={{ display: "flex", alignItems: "center", gap: 6, minHeight: 24, fontSize: 13, color: "var(--nw-ink)" }}>
      <span style={{ width: 18, flexShrink: 0, color: "var(--nw-ink-muted)", fontWeight: 600 }}>{r.n}</span>
      <span style={{ overflowWrap: "anywhere" }}>{r.name}</span>
      {r.captain && chip({ background: "var(--nw-navy)", color: "#FFFFFF" }, "C")}
      {r.vice && chip({ background: "var(--nw-navy-tint)", color: "var(--nw-navy)", border: "1px solid var(--nw-navy)" }, "VC")}
      {r.keeper && chip({ background: "var(--nw-ok)", color: "#FFFFFF" }, "WK")}
    </div>
  ));
  if (summary?.twelfth) {
    cells.push(
      <div key="twelfth" style={{ display: "flex", alignItems: "center", gap: 6, minHeight: 24, fontSize: 13, color: "var(--nw-ink)" }}>
        <span style={{ width: 18, flexShrink: 0, color: "var(--nw-ink-muted)", fontWeight: 600 }}>12</span>
        <span style={{ overflowWrap: "anywhere" }}>{summary.twelfth}</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>field</span>
      </div>,
    );
  }
  return (
    <div data-testid="plan-team" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="nw-section">Your team</div>
        <Link
          to={sheetPath(gameId)}
          data-testid="plan-team-edit"
          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 44, minHeight: 44, fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline" }}
        >
          Edit
        </Link>
      </div>
      {cells.length === 0 ? (
        <div className="nw-help" data-testid="plan-team-empty">No team picked yet. Edit to pick the eleven.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 10px" }}>
          <div>{cells.slice(0, half)}</div>
          <div>{cells.slice(half)}</div>
        </div>
      )}
      {summary && summary.openers.length > 0 && (
        <>
          <div style={{ height: 1, background: "var(--nw-line)" }} />
          <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>
            <b>Opening bowlers:</b> <span style={{ color: "var(--nw-ink-lede)" }}>{summary.openers.join(", ")}</span>
          </div>
        </>
      )}
      {summary?.volunteers && (
        <>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }} data-testid="plan-team-volunteers">
            <b>Volunteers:</b> <span style={{ color: "var(--nw-ink-lede)" }}>{summary.volunteers}</span>
          </div>
          <div data-testid="plan-team-volunteers-private" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--nw-ink-muted)" }}>
            <Lock size={12} aria-hidden="true" style={{ flexShrink: 0 }} />
            Volunteers are only for you. Players don&apos;t see them.
          </div>
        </>
      )}
    </div>
  );
}

/** Bat, Bowl or On the day: a switch in place of three 44px segments, as the board's `plan-toss`. */
function TossSwitch({ value, onChange }) {
  return (
    <div data-testid="plan-toss" role="radiogroup" aria-labelledby="plan-toss-label" style={{ display: "flex", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)", overflow: "hidden" }}>
      {TOSS_CHOICES.map((c, i) => {
        const on = value === c.value;
        return (
          <button
            key={c.value}
            type="button"
            role="radio"
            aria-checked={on}
            data-testid={c.testId}
            data-nav="in-place"
            onClick={() => onChange(c.value)}
            style={{
              flex: "1 1 0",
              minWidth: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 44,
              boxSizing: "border-box",
              padding: "6px 10px",
              border: "none",
              borderLeft: i === 0 ? "none" : "1px solid var(--nw-line)",
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.25,
              textAlign: "center",
              cursor: "pointer",
              background: on ? "var(--nw-navy)" : "var(--nw-surface)",
              color: on ? "#FFFFFF" : "var(--nw-ink-lede)",
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

/** One game-plan box: a bold title, its one-line hint and a textarea. */
function PlanText({ id, title, hint, minHeight, value, onChange }) {
  const uid = useId();
  const hintId = `${uid}-hint`;
  return (
    <div className="flex flex-col" style={{ gap: 4 }}>
      <label htmlFor={uid} style={{ fontSize: 15, fontWeight: 700, color: "var(--nw-ink)" }}>{title}</label>
      <div id={hintId} style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>{hint}</div>
      <textarea
        id={uid}
        data-testid={id}
        aria-describedby={hintId}
        value={value}
        onChange={onChange}
        maxLength={PLAN_TEXT_MAX}
        style={{
          width: "100%",
          minHeight,
          boxSizing: "border-box",
          padding: 12,
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-control)",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          fontSize: 15,
          lineHeight: 1.45,
          color: "var(--nw-ink)",
          resize: "vertical",
          outline: "none",
        }}
      />
    </div>
  );
}

/** Who sees what: one box, Players see the game plan. Parents and caregivers are left out (D297). */
function WhoSees({ checked, onToggle }) {
  return (
    <div data-testid="plan-who-sees" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
      <div className="nw-section">Who sees what</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          aria-labelledby="plan-players-see-label"
          data-testid="plan-players-see"
          data-nav="in-place"
          onClick={onToggle}
          style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, padding: 0, background: "transparent", border: "none", cursor: "pointer" }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 22,
              height: 22,
              boxSizing: "border-box",
              borderRadius: 6,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: checked ? "var(--nw-navy)" : "var(--nw-surface)",
              border: checked ? "none" : "2px solid var(--nw-line-strong)",
            }}
          >
            {checked && <Check size={14} strokeWidth={3} color="#FFFFFF" aria-hidden="true" />}
          </span>
        </button>
        <span id="plan-players-see-label" style={{ fontSize: 15, fontWeight: 600, color: "var(--nw-ink)" }}>Players see the game plan</span>
      </div>
      {checked ? (
        <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>The team and general notes go to everyone in the squad. Volunteers stay with you.</div>
      ) : (
        <div data-testid="plan-players-unticked" className="nw-help" style={{ lineHeight: 1.5 }}>{PLAN_KEPT_BACK}</div>
      )}
    </div>
  );
}

/** The board's card: a coloured left edge, a glyph, a title and one line. */
function Notice({ testId, tone, role, title, body }) {
  const alert = tone === "alert";
  const edge = alert ? "var(--nw-alert)" : "var(--nw-navy)";
  return (
    <div
      data-testid={testId}
      role={role}
      style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderLeft: `3px solid ${edge}`, borderRadius: "var(--nw-radius-control)" }}
    >
      {alert ? <TriangleAlert size={16} color={edge} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} /> : <Check size={16} color={edge} strokeWidth={2.5} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />}
      <div className="flex flex-col" style={{ gap: 2 }}>
        <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: "var(--nw-ink)" }}>{title}</span>
        <span className="nw-help" style={{ lineHeight: 1.5 }}>{body}</span>
      </div>
    </div>
  );
}

/** The step-1 checklist, asked once more before anything is sent. It never blocks: pressing Publish team again publishes. */
function PublishCheck({ gameId }) {
  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      <Notice testId="plan-publish-check" tone="alert" role="alert" title="No captain or keeper is tagged yet." body="Publish anyway, or go back and tag them." />
      <Link
        to={sheetPath(gameId)}
        data-testid="plan-publish-check-back"
        style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", minHeight: 44, boxSizing: "border-box", padding: "0 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-control)", fontSize: 14, fontWeight: 600, color: "var(--nw-navy)" }}
      >
        Back to Pick the XI
      </Link>
    </div>
  );
}
